import { useMutation, useQuery } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { callGet, callPost } from '@/api/fetcher';
import Loading from '@/components/atoms/Loading/Loading';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { toErrorReason } from '@/segment/errorReason';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { NamedRole } from '@/shared/types/role';
import { EventName, PageName } from '@/shared/types/segment';
import { getInvitationRoleNames } from '@/util/memberUtils';
import { roleDisplayDescription } from '@/util/systemRoles';

interface PendingUser {
  email: string;
  name: string;
  avatar?: string;
  invitation?: {
    invitation_code: string;
    account_name: string;
    account_avatar?: string;
    roles?: NamedRole[];
    role_ids?: string[];
  };
}

interface AcceptInvitationRequest {
  invitation_code: string;
}

interface AcceptInvitationResponse {
  success: boolean;
  redirect?: string;
  error?: string;
}

const isSafeRedirectUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
};

// API functions following the established pattern
const API = {
  getPendingUser: async (): Promise<{ pendingUser: PendingUser | null }> =>
    callGet('/www/api/auth/pending-user'),

  handleInvitation: async (data: AcceptInvitationRequest): Promise<AcceptInvitationResponse> =>
    callPost('/www/api/auth/invitation', data),
};

export const AcceptInvitationPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useSegments(PageName.ACCEPT_INVITATION);

  // Fetch pending user info using useQuery
  const {
    data: pendingUserData,
    isLoading,
    error: queryError,
  } = useQuery({ queryKey: ['auth', 'pending-user'], queryFn: API.getPendingUser, retry: false });

  const pendingUser = pendingUserData?.pendingUser;
  const invitedRoles = getInvitationRoleNames(pendingUser?.invitation ?? {}, t);

  // Accept invitation mutation
  const { mutate: acceptInvitation, isPending: isAccepting } = useMutation({
    mutationFn: API.handleInvitation,
    onSuccess: (response) => {
      if (response.success) {
        trackEvent(EventName.InvitationAccepted);
        toast.success(t('invitations.invitation_accepted'));
        // Add delay to ensure toast is visible before redirect
        setTimeout(() => {
          const redirectUrl =
            response.redirect && isSafeRedirectUrl(response.redirect) ? response.redirect : '/';
          window.location.href = redirectUrl;
        }, 500);
      } else {
        // A 200 the server declined; the mutation cache never sees it.
        trackEvent(EventName.ActionFailed, {
          action: 'invitation_accept',
          error_reason: 'validation',
        });
        toast.error(response.error || t('invitations.accept_failed'));
      }
    },
    onError: (error: Error) => {
      trackEvent(EventName.ActionFailed, {
        action: 'invitation_accept',
        error_reason: toErrorReason(error),
      });
      toast.error(error.message || t('invitations.accept_failed'));
    },
  });

  const handleAcceptInvitation = () => {
    if (!pendingUser?.invitation?.invitation_code) {
      return;
    }
    acceptInvitation({ invitation_code: pendingUser.invitation.invitation_code });
    trackEvent(EventName.OnboardingClickAcceptInvitation);
  };

  const handleDeclineInvitation = async () => {
    try {
      // Call the same endpoint but without invitation code to create user without sending invitation
      await API.handleInvitation({ invitation_code: '' });
    } catch (error) {
      console.error('Failed to decline invitation:', error);
    }
    trackEvent(EventName.OnboardingClickDeclineInvitation);
    // Navigate anyway to avoid blocking the user
    navigate('/');
  };

  if (isLoading) {
    return <Loading />;
  }

  if (queryError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>{t('common.error')}</CardTitle>
            <CardDescription>{t('invitations.load_failed')}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => navigate('/login')} className="w-full">
              {t('common.back_to_login')}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (!pendingUser || !pendingUser.invitation) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Left side - Invitation Form */}
      <div className="flex h-screen flex-1 items-center justify-center overflow-y-auto p-8">
        <Card className="h-full w-full max-w-144 border-0 shadow-none">
          <CardHeader className="space-y-1 pb-6">
            <h1 className="text-3xl font-bold">{t('invitations.accept_invitation')}</h1>
            <p className="text-foreground-muted">
              <Trans
                i18nKey="invitations.invitation_description"
                values={[pendingUser.invitation.account_name]}
              />
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* User Info Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">{t('invitations.your_details')}</h3>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <div className="text-sm font-medium text-foreground-muted">
                    {t('common.name')}
                  </div>
                  <div className="text-sm font-medium">{pendingUser.name}</div>
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium text-foreground-muted">
                    {t('common.email')}
                  </div>
                  <div className="text-sm font-medium">{pendingUser.email}</div>
                </div>
              </div>
            </div>

            {/* Account Info Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">{t('invitations.joining_organization')}</h3>

              <div className="flex items-center gap-4 rounded-lg border p-4">
                {pendingUser.invitation.account_avatar && (
                  <img
                    src={pendingUser.invitation.account_avatar}
                    alt={pendingUser.invitation.account_name}
                    className="h-12 w-12 rounded-full"
                  />
                )}
                <div>
                  <div className="font-medium">{pendingUser.invitation.account_name}</div>
                  {invitedRoles.length > 0 && (
                    <div className="mt-1 text-sm text-foreground-muted">
                      {t('invitations.role_prefix')}{' '}
                      {invitedRoles.map((role) => role.name).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Role Details (if any) */}
            {invitedRoles.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">{t('invitations.your_permissions')}</h3>

                <div className="space-y-3">
                  {invitedRoles.map((role) => {
                    const description = roleDisplayDescription(role, t);
                    return (
                      <div key={role.role_id} className="rounded-lg border bg-muted/20 p-4">
                        <div className="font-medium">{role.name}</div>
                        {description && (
                          <div className="mt-1 text-sm text-foreground-muted">{description}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>

          <CardFooter className="flex flex-col gap-3 pt-6 pb-10">
            <Button
              onClick={handleAcceptInvitation}
              isLoading={isAccepting}
              className="w-full"
              size="md"
            >
              {t('invitations.join_team')}
            </Button>
            <Button
              onClick={handleDeclineInvitation}
              variant="ghost"
              disabled={isAccepting}
              className="w-full"
            >
              {t('invitations.decline_invitation')}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};
